import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd);
  return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
}

export const SingleImageUpload = ({ value, onChange, testid = "admin-image-upload" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadFile(file));
      toast.success("图片已上传");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value && <img src={value} alt="" className="w-20 h-20 object-cover border border-white/15" data-testid="admin-image-preview" />}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testid} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="btn-ghost !py-2 !px-3 !text-[10px] disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
        {value ? "更换图片" : "上传图片"}
      </button>
    </div>
  );
};

export const GalleryUpload = ({ values = [], onAdd, onRemove, onReorder = () => {}, testid = "admin-gallery-upload" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) onAdd(await uploadFile(f));
      toast.success("图片已上传");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((v, i) => (
          <div
            key={i}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", String(i));
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
              if (isNaN(from) || from === i) return;
              const nv = [...values];
              const [moved] = nv.splice(from, 1);
              nv.splice(i, 0, moved);
              onReorder(nv);
            }}
            className="relative w-20 h-20 border border-white/15 group cursor-grab active:cursor-grabbing"
            data-testid={`admin-gallery-item-${i}`}
          >
            <img src={v} alt="" className="w-full h-full object-cover pointer-events-none" />
            <span className="absolute top-0.5 left-0.5 font-mono2 text-[8px] text-black bg-[#ffde00] px-1">{i + 1}</span>
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="absolute -top-2 -right-2 bg-[#ffde00] text-black rounded-none w-5 h-5 flex items-center justify-center cursor-pointer border-0"
              data-testid={`admin-gallery-remove-${i}`}
              aria-label="移除图片"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      <p className="font-zh text-[10px] text-[#52525b] mb-2">拖动图片可调整顺序，详情页按此顺序展示</p>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={testid} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="btn-ghost !py-2 !px-3 !text-[10px] disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
        上传现场图片（可多选）
      </button>
    </div>
  );
};
